import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MaterialReactTable from 'material-react-table';
import { useAxios } from '../../contexts/axios';
import { BACKEND_URL } from '../../configs';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { ExportToCsv } from 'export-to-csv';
import { useAuth } from '../../contexts/auth';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    Tooltip,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import {
    getTeamSelectTextFieldProps,
    normalizeTeamFieldForRow,
    normalizedTeamEditValue,
    teamCellDisplayLabel,
    teamSelectMenuItems,
} from './taggingTeamSelect';

export default function MongoTagging() {
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [validationErrors, setValidationErrors] = useState({});
    const axios = useAxios();
    const { isAdmin } = useAuth();
    function call(url, data, method) {
        const requestOptions = {
            url: `${BACKEND_URL}/mongo/clusters-metadata${url}`,
            method: method,
            data: data,
        };
        axios(requestOptions);
    }

    useEffect(() => {
        axios({ url: `${BACKEND_URL}/mongo/clusters-metadata` }).then(function (
            res
        ) {
            const rows = Array.isArray(res.data) ? res.data : [];
            setTableData(
                rows.map((row) => ({
                    ...row,
                    team: normalizeTeamFieldForRow(row?.team),
                }))
            );
        });
    }, []);

    const handleCreateNewRow = (values) => {
        if (values.name.length <= 2) {
            alert('Please enter valid value, too few characters');
            return;
        }

        tableData.push(values);
        setTableData([...tableData]);
        call(
            '',
            {
                cluster_id: values.cluster_id,
                name: values.name,
                instance_size: values.instance_size,
                max_instance_size: values.max_instance_size,
                min_instance_size: values.min_instance_size,
                diskSize: values.diskSize,
                updated_at: values.updated_at,
                vertical: values.vertical,
                team: values.team,
                owner: values.owner,
                application: values.application,
                mongoDBVersion: values.mongoDBVersion
            },
            'POST'
        );
    };

    const handleSaveRowEdits = async ({ exitEditingMode, row, values }) => {
        if (!Object.keys(validationErrors).length) {
            const merged = {
                ...row.original,
                team: values.team,
                application: values.application,
                owner: values.owner,
            };
            tableData[row.index] = merged;
            setTableData([...tableData]);
            exitEditingMode();

            call(
                '',
                {
                    ...row.original,
                    team: values.team,
                    application: values.application,
                    owner: values.owner,
                },
                'PUT'
            );
        }
    };

    const handleDeleteRow = useCallback(
        (row) => {
            if (
                !window.confirm(
                    `Are you sure you want to delete ${row.getValue('name')}`
                )
            ) {
                return;
            }
            const payload = { ...row.original };
            tableData.splice(row.index, 1);
            setTableData([...tableData]);
            call('', payload, 'DELETE');
        },
        [tableData]
    );

    const getCommonEditTextFieldProps = useCallback(
        (cell) => {
            return {
                error: !!validationErrors[cell.id],
                helperText: validationErrors[cell.id],
                onBlur: (event) => {
                    const isOptionalTag =
                        cell.column.id === 'team' ||
                        cell.column.id === 'application' ||
                        cell.column.id === 'owner';
                    const isValid =
                        cell.column.id === 'email'
                            ? validateEmail(event.target.value)
                            : isOptionalTag
                            ? true
                            : validateRequired(event.target.value);
                    if (!isValid) {
                        setValidationErrors({
                            ...validationErrors,
                            [cell.id]: `${cell.column.columnDef.header} is required`,
                        });
                    } else {
                        delete validationErrors[cell.id];
                        setValidationErrors({
                            ...validationErrors,
                        });
                    }
                },
            };
        },
        [validationErrors]
    );

    const columns = useMemo(
        () => [
            {
                accessorKey: 'cluster_id',
                header: 'CLUSTER ID',
                size: 140,
                enableEditing: false,
            },
            {
                accessorKey: 'name',
                header: 'CLUSTER NAME',
                size: 140,
                enableEditing: false,
            },
            {
                accessorKey: 'instance_size',
                header: 'INSTANCE SIZE',
                size: 140,
                enableEditing: false,
            },
            {
                accessorKey: 'max_instance_size',
                header: 'MAX INSTANCE SIZE',
                size: 140,
                enableEditing: false,
            },
            {
                accessorKey: 'min_instance_size',
                header: 'MIN INSTANCE SIZE',
                size: 140,
                enableEditing: false,
            },
            {
                accessorKey: 'diskSize',
                header: 'DISK SIZE',
                size: 140,
                enableEditing: false,
            },
            {
                accessorKey: 'updated_at',
                header: 'UPDATED AT',
                size: 140,
                enableEditing: false,
            },
            {
                accessorKey: 'team',
                header: 'TEAM',
                Cell: ({ cell }) => teamCellDisplayLabel(cell.getValue()),
                muiTableBodyCellEditTextFieldProps: ({ cell }) => ({
                    ...getTeamSelectTextFieldProps(),
                    select: true,
                    children: teamSelectMenuItems(),
                    ...getCommonEditTextFieldProps(cell),
                }),
            },
            {
                accessorKey: 'vertical',
                header: 'VERTICAL',
                enableEditing: false,
            },
            {
                accessorKey: 'application',
                header: 'APPLICATION',
                muiTableBodyCellEditTextFieldProps: ({ cell }) => ({
                    ...getCommonEditTextFieldProps(cell),
                }),
            },
            {
                accessorKey: 'owner',
                header: 'OWNER',
                muiTableBodyCellEditTextFieldProps: ({ cell }) => ({
                    ...getCommonEditTextFieldProps(cell),
                }),
            },
            {
                accessorKey: 'mongoDBVersion',
                header: 'VERSION',
                size: 140,
                enableEditing: false,
            },
        ],
        [getCommonEditTextFieldProps]
    );
    const csvOptions = {
        fieldSeparator: ',',
        quoteStrings: '"',
        decimalSeparator: '.',
        showLabels: true,
        useBom: true,
        filename: 'Mongo Tagging Data '+new Date().toDateString(),
        useKeysAsHeaders: false,
        headers: columns.map((c) => c.header),
    };

    const csvExporter = new ExportToCsv(csvOptions);
    //console.log(tableData);

    const handleExportData = () => {
        var data = [];
        for (var i = 0; i < tableData.length; i++) {
            data.push({
                cluster_id: tableData[i]['cluster_id'],
                name: tableData[i]['name'],
                instance_size: tableData[i]['instance_size'],
                max_instance_size: tableData[i]['max_instance_size'],
                min_instance_size: tableData[i]['min_instance_size'],
                diskSize: tableData[i]['diskSize'],
                updated_at: tableData[i]['updated_at'],
                team: tableData[i]['team'],
                vertical: tableData[i]['vertical'],
                application: tableData[i]['application'],
                owner: tableData[i]['owner'],
                mongoDBVersion: tableData[i]['mongoDBVersion'],
            });
        }
        csvExporter.generateCsv(data);
    };
    return (
        <>
            <MaterialReactTable
                displayColumnDefOptions={{
                    'mrt-row-actions': {
                        muiTableHeadCellProps: {
                            align: 'center',
                        },
                        size: 120,
                    },
                }}
                columns={columns}
                data={tableData}
                editingMode="modal"
                enableColumnOrdering
                enableStickyFooter
                enableStickyHeader
                enableEditing
                enableDensityToggle={false}
                initialState={{
                    columnPinning: {
                        left: ['mrt-row-actions','name'],
                    },
                    density: 'compact',
                    columnVisibility: { cluster_id: false },
                }}
                enableRowActions
                renderRowActions={({ row, table }) => (
                    <Box sx={{ display: 'flex', gap: '1rem' }}>
                        <Tooltip arrow placement="left" title="Edit">
                            <IconButton
                                disabled={!isAdmin()}
                                onClick={() => table.setEditingRow(row)}
                            >
                                <Edit />
                            </IconButton>
                        </Tooltip>
                        <Tooltip arrow placement="right" title="Delete">
                            <IconButton
                                disabled={!isAdmin()}
                                color="error"
                                onClick={() => handleDeleteRow(row)}
                            >
                                <Delete />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
                positionToolbarAlertBanner="bottom"
                renderTopToolbarCustomActions={() => (
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '1rem',
                            p: '0.5rem',
                            flexWrap: 'wrap',
                        }}
                    >
                        <Button
                            color="success"
                            onClick={() => setCreateModalOpen(true)}
                            variant="contained"
                            disabled={!isAdmin()}
                        >
                            Add New Cluster
                        </Button>
                        <Button
                            color="primary"
                            //export all data that is currently in the table (ignore pagination, sorting, filtering, etc.)
                            onClick={handleExportData}
                            startIcon={<FileDownloadIcon />}
                            variant="contained"
                        >
                            Export All Data
                        </Button>
                    </Box>
                )}
                onEditingRowSave={handleSaveRowEdits}
            />
            <AddNewUser
                columns={columns}
                open={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                onSubmit={handleCreateNewRow}
            />
        </>
    );
}

export const AddNewUser = ({ open, columns, onClose, onSubmit }) => {
    const [values, setValues] = useState(() =>
        columns.reduce((acc, column) => {
            acc[column.accessorKey ?? ''] = '';
            return acc;
        }, {})
    );
    const handleSubmit = () => {
        onSubmit(values);
        onClose();
    };

    return (
        <Dialog open={open}>
            <DialogTitle textAlign="center">Add Cluster Details</DialogTitle>
            <DialogContent>
                <form onSubmit={(e) => e.preventDefault()}>
                    <Stack
                        sx={{
                            width: '100%',
                            minWidth: { xs: '300px', sm: '360px', md: '400px' },
                            gap: '1.5rem',
                        }}
                    >
                        {columns.map(function (object, i) {
                            if (object.accessorKey === 'team') {
                                return (
                                    <TextField
                                        {...getTeamSelectTextFieldProps()}
                                        key="team"
                                        select
                                        fullWidth
                                        label={object.header}
                                        name="team"
                                        value={normalizedTeamEditValue(
                                            values.team
                                        )}
                                        onChange={(e) =>
                                            setValues({
                                                ...values,
                                                team: e.target.value,
                                            })
                                        }
                                    >
                                        {teamSelectMenuItems()}
                                    </TextField>
                                );
                            }
                            return (
                                <TextField
                                    key={object.accessorKey}
                                    label={object.header}
                                    name={object.accessorKey}
                                    onChange={(e) =>
                                        setValues({
                                            ...values,
                                            [e.target.name]: e.target.value,
                                        })
                                    }
                                />
                            );
                        })}
                    </Stack>
                </form>
            </DialogContent>
            <DialogActions sx={{ p: '1.25rem' }}>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    color="secondary"
                    onClick={handleSubmit}
                    variant="contained"
                >
                    Add New Cluster
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const validateRequired = (value) => !!value.length;
const validateEmail = (email) =>
    !!email.length &&
    email
        .toLowerCase()
        .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        );
